// App.js — koristi AppContent i postavlja kontekst
import React, { useState, useEffect, useRef, createContext } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { getAuth, setPersistence, browserLocalPersistence, getRedirectResult } from "firebase/auth";
import { useJsApiLoader } from "@react-google-maps/api";
import TagManager from 'react-gtm-module';

import AppContent from "./AppContent";
import { ConsentProvider } from "./context/ConsentContext";
import { AccessProvider } from "./context/AccessContext";
import { auth } from "./firebase";
import { HelmetProvider } from "react-helmet-async";


import "./i18n";
import "./index.css";

export const UserContext = createContext(null);

const libraries = ['places'];

const App = () => {
  const headerRef = useRef(null);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [headerHeight, setHeaderHeight] = useState(0);
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [rightMenuOpen, setRightMenuOpen] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.classList.toggle("dark", theme === "dark");
    document.body.classList.toggle("light", theme === "light");
  }, [theme]);

  useEffect(() => {
    const authInstance = getAuth();
    const pendingLogin = sessionStorage.getItem('pending_login');

    const persistencePromise = setPersistence(authInstance, browserLocalPersistence)
      .then(() => console.log("✅ Persistence set"))
      .catch((err) => console.error("❌ Persistence error:", err));

    const redirectPromise = new Promise((resolve) => {
        getRedirectResult(authInstance)
          .then(async (result) => {
            if (result?.user) {
              try {
                const res = await fetch("https://modularobjects.com/checkout/api/user/firebase-user", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    uid: result.user.uid,
                    email: result.user.email,
                    displayName: result.user.displayName,
                    photoURL: result.user.photoURL,
                    providerId: result.user.providerData[0]?.providerId,
                    lastLoginAt: result.user.metadata?.lastSignInTime || new Date().toISOString()
                  })
                });
                if (!res.ok) throw new Error("Backend error");
                setUser(result.user);
                sessionStorage.removeItem('pending_login');
              } catch (err) {
                console.error("❌ Backend error:", err);
              }
            }
            resolve();
          })
          .catch((err) => {
            console.error("❌ Redirect error:", err);
            resolve();
          });
    });

    Promise.all([persistencePromise, redirectPromise])
      .then(() => console.log("🔧 Auth flow ready"));

    const unsubscribe = authInstance.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        //console.log("👤 Auth change:", firebaseUser.uid);
        setUser(firebaseUser);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);


  return (
    <HelmetProvider>
    <ConsentProvider>
      <UserContext.Provider value={{ user, setUser }}>
        <AccessProvider>
        <Router>
          <AppContent
            user={user}
            setUser={setUser}
            headerRef={headerRef}
            headerHeight={headerHeight}
            isLoaded={isLoaded}
            theme={theme}
            setTheme={setTheme}
            leftMenuOpen={leftMenuOpen}
            setLeftMenuOpen={setLeftMenuOpen}
            rightMenuOpen={rightMenuOpen}
            setRightMenuOpen={setRightMenuOpen}
          />
        </Router>
        </AccessProvider>
      </UserContext.Provider>
    </ConsentProvider>
    </HelmetProvider>
  );
};

export default App;